import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { themeBrightnessLabelKey, themeFamilyLabelKey, type ThemeDefinition } from "./themeCatalog";
import { ThemePreview } from "./ThemePreview";
import "./themeList.css";
import { KEY_ARROW_DOWN, KEY_ARROW_LEFT, KEY_ARROW_RIGHT, KEY_ARROW_UP, KEY_ENTER, KEY_SPACE } from "../ui/keyboardKeys";

/** Name eines Themes aus Familie und Helligkeit, z. B. „Noir dunkel“. */
export function themeDisplayName(definition: ThemeDefinition, tr: (key: string) => string): string {
  return `${tr(themeFamilyLabelKey(definition.family))} ${tr(themeBrightnessLabelKey(definition.brightness))}`;
}

export type ThemeListItem = {
  definition: ThemeDefinition;
  /** Abweichender Name, z. B. „wie App-Standard“. */
  label?: string;
  badges?: string[];
  disabled?: boolean;
  /** Bedienelemente rechts in der Zeile, z. B. Freigabe und Standard in der Systemadministration. */
  trailing?: ReactNode;
  key?: string;
};

type ThemeListProps = {
  items: ThemeListItem[];
  tr: (key: string) => string;
  label: string;
  /** Gewählte Zeile (Schlüssel), nur bei auswählbarer Liste. */
  selectedKey?: string | null;
  /** Macht die Liste zur Auswahl (Radiogruppe als Kachelraster): Klick und Pfeiltasten wählen. */
  onSelect?: (key: string, definition: ThemeDefinition) => void;
  /** Enter in einer auswählbaren Liste. */
  onConfirm?: () => void;
};

const keyOf = (item: ThemeListItem) => item.key ?? item.definition.id;

/**
 * Anzeigereihenfolge: Sondereinträge mit eigenem Schlüssel (z. B. „wie App-Standard“) zuerst, dann
 * alphabetisch nach Familienname in der Sprache des Benutzers, innerhalb der Familie hell vor dunkel.
 */
function sortThemeItems(items: ThemeListItem[], tr: (key: string) => string): ThemeListItem[] {
  const special = (item: ThemeListItem) => (item.key && item.key !== item.definition.id ? 0 : 1);
  const family = (item: ThemeListItem) => tr(themeFamilyLabelKey(item.definition.family));
  return [...items].sort((a, b) =>
    special(a) - special(b)
    || family(a).localeCompare(family(b))
    || (a.definition.brightness === b.definition.brightness ? 0 : a.definition.brightness === "light" ? -1 : 1));
}

/** Spaltenzahl des Kachelrasters, damit Pfeil hoch/runter eine Zeile springt. */
function gridColumns(list: HTMLElement | null): number {
  if (!list || typeof getComputedStyle === "undefined") return 1;
  return Math.max(1, getComputedStyle(list).gridTemplateColumns.split(" ").filter(Boolean).length);
}

/**
 * Theme-Auswahl mit verkleinerter Seitenvorschau je Theme. Auswählbar als Kachelraster (Radiogruppe,
 * Pfeiltasten, eine Kachel im Tab-Fokus), sonst eine Liste mit Vorschaubild und Bedienelementen je Zeile.
 */
export function ThemeList({ items: unsortedItems, tr, label, selectedKey = null, onSelect, onConfirm }: ThemeListProps) {
  const items = sortThemeItems(unsortedItems, tr);
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectable = Boolean(onSelect);
  const enabled = items.filter((item) => !item.disabled);
  const focusKey = selectedKey ?? (enabled[0] ? keyOf(enabled[0]) : null);

  const move = (event: KeyboardEvent, from: ThemeListItem) => {
    if (!onSelect) return;
    if (event.key === KEY_ENTER) {
      event.preventDefault();
      onConfirm?.();
      return;
    }
    if (event.key === KEY_SPACE) {
      event.preventDefault();
      onSelect(keyOf(from), from.definition);
      return;
    }
    const row = gridColumns(listRef.current);
    const step = event.key === KEY_ARROW_RIGHT ? 1 : event.key === KEY_ARROW_LEFT ? -1 : event.key === KEY_ARROW_DOWN ? row : event.key === KEY_ARROW_UP ? -row : 0;
    if (!step || enabled.length === 0) return;
    event.preventDefault();
    const index = enabled.findIndex((item) => keyOf(item) === keyOf(from));
    const next = enabled[Math.min(enabled.length - 1, Math.max(0, index + step))];
    onSelect(keyOf(next), next.definition);
    listRef.current?.querySelector<HTMLElement>(`[data-theme-key="${keyOf(next)}"]`)?.focus();
  };

  return (
    <div ref={listRef} className={`mq-theme-list${selectable ? " mq-theme-list--grid" : ""}`} role={selectable ? "radiogroup" : "list"} aria-label={label}>
      {items.map((item) => {
        const key = keyOf(item);
        const selected = selectable && key === selectedKey;
        const interactive = selectable && !item.disabled;
        return (
          <div
            key={key}
            data-theme-key={key}
            className={`mq-theme-row${selected ? " mq-theme-row--selected" : ""}${item.disabled ? " mq-theme-row--disabled" : ""}${interactive ? " mq-theme-row--interactive" : ""}`}
            role={selectable ? "radio" : "listitem"}
            aria-checked={selectable ? selected : undefined}
            aria-disabled={selectable ? item.disabled : undefined}
            tabIndex={interactive ? (key === focusKey ? 0 : -1) : undefined}
            onClick={interactive ? () => onSelect?.(key, item.definition) : undefined}
            onKeyDown={interactive ? (event) => move(event, item) : undefined}
          >
            <span className="mq-theme-row-preview">
              <ThemePreview definition={item.definition} title={tr(themeFamilyLabelKey(item.definition.family))} />
            </span>
            <span className="mq-theme-row-text">
              <span className="mq-theme-row-name">{item.label ?? themeDisplayName(item.definition, tr)}</span>
              {item.badges?.map((badge) => <span key={badge} className="mq-theme-row-badge">{badge}</span>)}
            </span>
            {item.trailing ? <span className="mq-theme-row-trailing" onClick={(event) => event.stopPropagation()}>{item.trailing}</span> : null}
          </div>
        );
      })}
    </div>
  );
}
